import { useState } from "react";
import { useScenario } from "@/contexts/ScenarioContext";
import { useI18n } from "@/contexts/I18nContext";
import { Calendar, ChevronDown, Check, Activity, Skull, Zap, ShieldCheck } from "lucide-react";

const PROFILE_META = {
  pristine: { color: "#10B981", icon: ShieldCheck, key: "scenario.profile.pristine" },
  outage:   { color: "#EF4444", icon: Skull,       key: "scenario.profile.outage" },
  incident: { color: "#F59E0B", icon: Zap,         key: "scenario.profile.incident" },
  mixed:    { color: "#3B82F6", icon: Activity,    key: "scenario.profile.mixed" },
};

export default function ScenarioSelector() {
  const { scenarios, scenario, switchScenario } = useScenario();
  const { locale, t } = useI18n();
  const [open, setOpen] = useState(false);

  if (!scenario) return null;
  const meta = PROFILE_META[scenario.profile] || PROFILE_META.mixed;

  const labelOf = (s) => (locale === "pt" ? s.label_pt : s.label);

  return (
    <div className="relative" data-testid="scenario-selector">
      <button
        data-testid="scenario-selector-btn"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="flex items-center gap-2.5 px-3 py-2 border border-slate-800 hover:border-[#1E3A8A] bg-[#0F172A] rounded-sm transition-colors min-w-[230px]"
      >
        <div className="w-7 h-7 flex items-center justify-center border flex-shrink-0" style={{ borderColor: meta.color + "55", background: meta.color + "12" }}>
          <meta.icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
        </div>
        <div className="text-left flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-mono">
            {t("header.scenario")}
          </div>
          <div className="text-xs font-semibold truncate">{labelOf(scenario)}</div>
        </div>
        <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 w-[360px] border border-slate-800 bg-[#0F172A] rounded-sm shadow-2xl z-50 max-h-[460px] overflow-y-auto" data-testid="scenario-dropdown">
          <div className="px-4 py-2 border-b border-slate-800 text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono flex items-center gap-2">
            <Calendar className="w-3 h-3" /> H1 2026 · 10 simulated days
          </div>
          {scenarios.map((s) => {
            const m = PROFILE_META[s.profile] || PROFILE_META.mixed;
            const isActive = scenario.id === s.id;
            const Icon = m.icon;
            return (
              <button
                key={s.id}
                data-testid={`scenario-option-${s.id}`}
                onMouseDown={(e) => { e.preventDefault(); switchScenario(s.id); setOpen(false); }}
                className={`w-full text-left px-4 py-3 border-b border-slate-800/60 hover:bg-[#1E293B] flex items-center gap-3 ${
                  isActive ? "bg-[#1E3A8A]/15" : ""
                }`}
              >
                <div className="w-8 h-8 flex items-center justify-center border flex-shrink-0" style={{ borderColor: m.color + "55", background: m.color + "10" }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: m.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-100">{labelOf(s)}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] font-mono font-bold uppercase tracking-wider" style={{ color: m.color }}>
                      {t(m.key)}
                    </span>
                    <span className="text-[10px] text-slate-500 truncate">· {s.description}</span>
                  </div>
                </div>
                {isActive && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
